import { isValidAcvParentMessageOrigin } from '../utils/acvMessageOrigin';
import { getAcvParentOriginForPostMessage } from '../utils/acvParentOrigin';

/**
 * fetch() for the AI Recording backend, with the ACV sign-in token.
 *
 * ACV posts the portal's sign-in token (Microsoft Graph or Google access token) into the iframe;
 * main.tsx stores it as `access_token`. The backend verifies it and takes the user's identity
 * only from it (backend/src/middleware/userMiddleware.ts).
 *
 * Tokens expire (Microsoft ~1h). On a 401 inside ACV we ask ACV for a fresh one
 * (`airecording-request-token`; ACV re-posts the identity) and retry once. Concurrent requests
 * share one refresh. When no fresh token can be had — a share link opened in its own tab
 * (not inside ACV) with a token left over from an earlier ACV session, or ACV could not renew
 * it — the rejected token is dropped and the request is sent once more as a signed-out viewer:
 * public pages then work as they do in a fresh browser, and the page sees the sign-out.
 *
 * A 503 VERIFY_UNAVAILABLE means the backend could not check the sign-in at that moment
 * (Microsoft / Google / ACV did not answer); reads are retried up to three times (about 10 s),
 * changes once.
 */
const TOKEN_KEY = 'access_token';
const REFRESH_TIMEOUT_MS = 6000;
const UNAVAILABLE_RETRY_MS = 1000;
const READ_RETRY_PAUSES_MS = [1000, 3000, 6000];

export const getAccessToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    return token && token !== 'undefined' && token !== 'null' ? token : null;
  } catch {
    return null;
  }
};

const isEmbedded = () => {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
};

let refreshing: Promise<boolean> | null = null;

/** Asks ACV for a fresh token; resolves true once a different token has been stored. */
export function requestFreshToken(): Promise<boolean> {
  if (!isEmbedded()) return Promise.resolve(false);
  if (refreshing) return refreshing;

  const previous = getAccessToken();
  refreshing = new Promise<boolean>((resolve) => {
    let settled = false;
    let timer = 0;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearInterval(timer);
      window.removeEventListener('message', onMessage);
      resolve(ok);
    };
    // ACV answers by re-posting the identity (main.tsx stores it). The same token again, or
    // none, means ACV has nothing fresher: don't wait out the timeout.
    const onMessage = (event: MessageEvent) => {
      if (!isValidAcvParentMessageOrigin(event.origin)) return;
      const user = event.data?.message?.isFromACV ? event.data.message.user : undefined;
      if (user && (!user.accessToken || user.accessToken === previous)) finish(false);
    };
    window.addEventListener('message', onMessage);
    try {
      window.parent.postMessage({ type: 'airecording-request-token' }, getAcvParentOriginForPostMessage() ?? '*');
    } catch {
      finish(false);
      return;
    }
    const startedAt = Date.now();
    timer = window.setInterval(() => {
      const current = getAccessToken();
      if (current && current !== previous) finish(true);
      else if (Date.now() - startedAt > REFRESH_TIMEOUT_MS) finish(false);
    }, 100);
  }).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Forgets a token the backend rejected; `airec-signed-out` lets the page drop the signed-in state. */
const dropAccessToken = () => {
  if (!getAccessToken()) return; // already dropped by a concurrent request
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing stored */
  }
  window.dispatchEvent(new CustomEvent('airec-signed-out'));
};

/**
 * After a 401: true when the request should be sent again — with a fresh token from ACV, or
 * without the rejected one when none can be had. Missing/expired tokens only (not e.g. a
 * wrong password). AUTH_REQUIRED means no token was sent, so there is nothing to drop;
 * TOKEN_INVALID means this request sent one, so it is retried without it even when a
 * concurrent request already dropped it.
 */
const recoverSignIn = async (response: Response): Promise<boolean> => {
  if (response.status !== 401) return false;
  const code = await errorCode(response);
  if (code !== 'TOKEN_INVALID' && code !== 'AUTH_REQUIRED') return false;
  if (await requestFreshToken()) return true;
  if (code !== 'TOKEN_INVALID') return false;
  dropAccessToken();
  return true;
};

const withToken = (init: RequestInit): RequestInit => {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

const errorCode = (response: Response): Promise<unknown> =>
  response.clone().json().then((b) => b?.code).catch(() => undefined);

/**
 * The server refused because of Admin Center Archive Mode (read-only / blocked / Legal Hold):
 * the app re-reads the school's state (src/hooks/useArchiveAccess.ts) so the screen matches.
 */
const announceArchiveRefusal = async (response: Response) => {
  if (response.status !== 403 && response.status !== 423) return;
  const code = await errorCode(response);
  if (typeof code === 'string' && /^(ARCHIVE_|LEGAL_HOLD)/.test(code)) {
    window.dispatchEvent(new CustomEvent('airec-archive-refusal', { detail: code }));
  }
};

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  let response = await fetch(input, withToken(init));
  // Reads (page loads) wait longer — e.g. ACV's backend still starting — so the page recovers
  // without a reload; changes are retried once.
  const read = !init.method || /^(GET|HEAD)$/i.test(init.method);
  const pauses = read ? READ_RETRY_PAUSES_MS : [UNAVAILABLE_RETRY_MS];
  for (const pause of pauses) {
    if (response.status !== 503 || (await errorCode(response)) !== 'VERIFY_UNAVAILABLE') break;
    await new Promise((resolve) => setTimeout(resolve, pause));
    response = await fetch(input, withToken(init));
  }
  void announceArchiveRefusal(response);
  return (await recoverSignIn(response)) ? fetch(input, withToken(init)) : response;
}

export interface UploadOptions {
  /** Share of the body sent so far (0 – 1). */
  onProgress?: (sent: number) => void;
  signal?: AbortSignal;
}

/** One POST of `body` with upload progress (fetch can't report it); resolves with the reply. */
const sendWithProgress = (url: string, body: FormData, { onProgress, signal }: UploadOptions) =>
  new Promise<Response>((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('Upload cancelled', 'AbortError')); return; }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    xhr.onload = () => resolve(new Response(xhr.responseText, {
      status: xhr.status,
      headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json' },
    }));
    xhr.onerror = () => reject(new TypeError('Network error'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });

/**
 * authFetch for uploads: POSTs `body` with progress and cancel (`signal`). Same sign-in handling;
 * a retry sends the file again (only when the sign-in had to be refreshed or checked again).
 */
export async function authUpload(url: string, body: FormData, options: UploadOptions = {}): Promise<Response> {
  let response = await sendWithProgress(url, body, options);
  if (response.status === 503 && (await errorCode(response)) === 'VERIFY_UNAVAILABLE') {
    await new Promise((resolve) => setTimeout(resolve, UNAVAILABLE_RETRY_MS));
    response = await sendWithProgress(url, body, options);
  }
  void announceArchiveRefusal(response);
  return (await recoverSignIn(response)) ? sendWithProgress(url, body, options) : response;
}
